import {
  createAsyncThunk,
  createSlice,
  current,
  PayloadAction,
} from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { URL } from '../../constants/url'
import axiosInstance from '../../utils/axiosInstance'
import { filterReportsForDriverVisibility } from '../../utils/filterReportsForDriver'
import { openPdf } from '../../utils/openPdf'
import { RootState } from '../store'
import type {
  GroupedCargosResponse,
  ReportCargoDirection,
  ReportCargosClientOrderBody,
} from '../types/reportGroupedCargos'

export interface Expense {
  id?: number
  type: string
  payment_type: 'cash' | 'cash_check' | 'card' | 'card_check' | 'fuel_card'
  date: string
  quantity?: number
  amount?: number
  currency?: string
  description?: string
  images?: {
    original: string
    lg: string
    md: string
    sm: string
  }[]
}

export interface Incoming {
  amount: number
  currency: string
  date: string
  description: null | string
  id: number
}

// Начальное состояние

interface Vehicle {
  id: number
  name: string
}

export interface Report {
  id: number
  status: string
  user: User
  avg_cons: number
  fuel_before: number
  fuel_after: number
  departure: string
  mileage_before: number | null
  mileage_after: number | null
  mileage_full: number
  car: Vehicle | null
  trailer: Vehicle
  crossing_border_there: string
  crossing_border_back: string
  reimbursement_date: string
  expenses: Expense[]
  incomings: Incoming[]
  all_expenses: string | number
  arrival: string
  issued_byn: number
  issued_byn_balance: number
  issued_eur: number
  issued_eur_balance: number
  issued_rub: number
  issued_rub_balance: number
  issued_usd: number
  issued_usd_balance: number
  report_pdf_url: string
  report_pdf_advance_url: string
  report_images_url: string
  report_pdf_business_trip_url: string
  report_pdf_order_url: string
  report_pdf_driver_card_url: string
  report_pdf_delivery_url: string
  number: string
  from: { id: number; name: string; country: { iso: string; name: string } }
  to: { id: number; name: string; country: { iso: string; name: string } }
  job_title?: string | null
  travel_purpose?: string | null
  travel_allowance?: number | null
  cargos_total?: number
  cargos_total_cash?: number
  cargos_total_bank?: number
}

export interface User {
  id: number
  name: string
  email: string
  avatar: {
    lg: string
    md: string
    original: string
    sm: string
  }[]
  active: boolean
  role: string
  login?: string
  phone?: string
  phone2?: string
  registration?: string
}

export interface Car {
  id: number
  name: string
  active: boolean
  image: {
    lg: string
    md: string
    original: string
    sm: string
  }
}

export interface Receipts {
  [key: string]: string[]
}

export type ReportsSummary = {
  unbilled: number
  unpaid: number
  on_payment: number
  total: number
  transport_expenses: number
  all_expenses: number
  distance: number
}

interface InitState {
  isPendingGetReports: boolean
  isPendingGetAllReports: boolean
  isPendingGetCurrentReports: boolean

  isVisibleModalReport: boolean
  isVisibleDeleteModalReport: boolean
  currentReport: Report | null
  reports: Report[]
  // expenses
  currentExpenses: Expense | null
  currentIncoming: Expense | null

  expenses: Expense[] | []
  isLoading: boolean
  error: string | null
  isPendingAddExpense: boolean
  isFulfilledAddExpense: boolean
  isRejectAddExpense: boolean
  isPendingDeleteExpense: boolean
  isFulfilledDeleteExpense: boolean
  isRejectDeleteExpense: boolean
  isVisibleDeleteModalExpenses: boolean
  isVisibleDeleteModalIncoming: boolean
  isFulfilledDeleteIncoming: boolean
  isRejectDeleteIncoming: boolean
  isPendingDeleteIncoming: boolean
  lastReportsQuery: any
  currentReportId: number | string | null
  isPendingAddIncoming: boolean
  isRejectAddIncoming: boolean
  groupedCargosForward: GroupedCargosResponse | null
  groupedCargosBackward: GroupedCargosResponse | null
  isPendingGroupedCargos: boolean
  isPendingReportCargosClientOrder: boolean
  reportModalInitialTab: number | null
  reportsSummary: ReportsSummary | null
  isPendingGetReportsSummary: boolean
  reportsSummaryRequestId: string | null
}

const initialState: InitState = {
  isPendingGetReports: false,
  isPendingGetAllReports: false,
  isPendingGetCurrentReports: false,
  isVisibleModalReport: false,
  isVisibleDeleteModalReport: false,
  currentReport: null,
  reports: [],
  // expenses
  currentExpenses: null,
  currentIncoming: null,
  expenses: [],
  isLoading: false,
  error: null,
  isPendingAddExpense: false,
  isFulfilledAddExpense: false,
  isRejectAddExpense: false,
  isPendingDeleteExpense: false,
  isFulfilledDeleteExpense: false,
  isRejectDeleteExpense: false,
  isVisibleDeleteModalExpenses: false,
  isVisibleDeleteModalIncoming: false,
  isFulfilledDeleteIncoming: false,
  isPendingDeleteIncoming: false,
  isRejectDeleteIncoming: false,
  isPendingAddIncoming: false,
  isRejectAddIncoming: false,
  groupedCargosForward: null,
  groupedCargosBackward: null,
  isPendingGroupedCargos: false,
  isPendingReportCargosClientOrder: false,
  reportModalInitialTab: null,
  lastReportsQuery: [],
  currentReportId: null,
  reportsSummary: null,
  isPendingGetReportsSummary: false,
  reportsSummaryRequestId: null,
}

interface ReportsQuery {
  cars?: string[]
  employees?: string[]
  status?: string[]
  dateFrom?: Date | string | null
  dateTo?: Date | string | null
  orderBy?: string
  orderDirection?: string
}

function formatReportsQueryDate(date: Date | string) {
  const newDate = new Date(date)
  return newDate
    .toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Europe/Moscow',
    })
    .split('.')
    .reverse()
    .join('-')
}

function buildReportsQueryParams({
  cars,
  employees,
  dateFrom,
  dateTo,
  status,
}: ReportsQuery) {
  const params = new URLSearchParams()

  if (cars?.length) params.append('car_ids', cars.join(','))
  if (employees?.length) params.append('user_ids', employees.join(','))
  if (status?.length) params.append('status', status.join(','))
  if (dateFrom) params.append('date_from', formatReportsQueryDate(dateFrom))
  if (dateTo) params.append('date_to', formatReportsQueryDate(dateTo))
  params.append('order_direction', 'desc')

  return params
}

export const fetchReportsSummary = createAsyncThunk(
  'reports/fetchReportsSummary',
  async (query: ReportsQuery, { rejectWithValue }) => {
    const params = buildReportsQueryParams(query)

    try {
      const response = await axiosInstance.get(`/${URL.report}/summary`, {
        params,
      })

      if (response.statusText === 'OK' || response.status === 200) {
        return response.data as ReportsSummary
      }
      throw new Error(response.statusText)
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data)
      }
      console.error('An unexpected error occurred:', error)
      return rejectWithValue('An unexpected error occurred')
    }
  }
)

export const fetchReports = createAsyncThunk(
  'reports/fetchReports',
  async (query: ReportsQuery, { rejectWithValue, dispatch }) => {
    const params = buildReportsQueryParams(query)
    void dispatch(fetchReportsSummary(query))

    try {
      const response = await axiosInstance.get(`/${URL.report}`, { params })

      if (response.statusText === 'OK') {
        return response.data
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data)
      } else {
        console.error('An unexpected error occurred:', error)
        return rejectWithValue('An unexpected error occurred')
      }
    }
  }
)

export const fetchAllReports = createAsyncThunk(
  'reports/fetchAllReports',
  async (_, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      params.append('order_direction', 'desc')

      const response = await axiosInstance.get(`/${URL.report}`, { params })

      if (response.statusText === 'OK') {
        return response.data
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data)
      } else {
        console.error('An unexpected error occurred:', error)
        return rejectWithValue('An unexpected error occurred')
      }
    }
  }
)

const sortByDate = (arr: Report[]) => {
  return Boolean(arr.length)
    ? arr.sort((a, b) => {
      return new Date(b.departure).getTime() - new Date(a.departure).getTime()
    })
    : arr
}

export const fetchCurrentReport = createAsyncThunk(
  'reports/fetchCurrentReport',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/${URL.report}/${id}`)

      if (response.statusText === 'OK') {
        return response.data
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data)
      } else {
        console.error('An unexpected error occurred:', error)
        return rejectWithValue('An unexpected error occurred')
      }
    }
  }
)

export type CarLastTripPrefill = {
  mileage_after: number | string | null
  fuel_after: number | string | null
}

function extractReportsList(payload: unknown): Report[] {
  if (Array.isArray(payload)) {
    return payload
  }
  if (!payload || typeof payload !== 'object') {
    return []
  }
  const data = payload as Record<string, unknown>
  const nested =
    data.data && typeof data.data === 'object'
      ? (data.data as Record<string, unknown>)
      : null
  const candidates = [data.data, data.reports, nested?.data]
  const list = candidates.find((item) => Array.isArray(item))
  return Array.isArray(list) ? (list as Report[]) : []
}

function reportCarId(report: Report) {
  const withCarId = report as Report & { car_id?: number | string }
  return withCarId.car?.id ?? withCarId.car_id
}

function reportSortTime(report: Report) {
  const raw = report.arrival || report.departure
  const time = raw ? new Date(raw).getTime() : NaN
  return Number.isFinite(time) ? time : 0
}

function hasTripValue(value: number | string | null | undefined) {
  return value !== null && value !== undefined && String(value) !== ''
}

export function pickLastTripPrefill(
  list: Report[],
  {
    carId,
    excludeReportId,
    beforeArrival,
  }: {
    carId?: number | string | null
    excludeReportId?: number | string | null
    beforeArrival?: string | null
  } = {}
): CarLastTripPrefill {
  const beforeTime = beforeArrival ? new Date(beforeArrival).getTime() : NaN

  const sameCar = list.filter((report) => {
    if (
      excludeReportId != null &&
      Number(report.id) === Number(excludeReportId)
    ) {
      return false
    }
    if (carId == null || carId === '') {
      return true
    }
    const id = reportCarId(report)
    if (id == null) {
      return false
    }
    return Number(id) === Number(carId)
  })

  const withValues = sameCar.filter(
    (report) =>
      hasTripValue(report.mileage_after) || hasTripValue(report.fuel_after)
  )

  const byArrival = (reports: Report[]) => {
    const sorted = [...reports].sort(
      (a, b) => reportSortTime(b) - reportSortTime(a)
    )
    if (!sorted.length) {
      return undefined
    }
    if (!Number.isFinite(beforeTime)) {
      return sorted[0]
    }
    return (
      sorted.find((report) => reportSortTime(report) <= beforeTime) || sorted[0]
    )
  }

  const lastReport =
    byArrival(
      withValues.filter(
        (report) => report.status === 'sent' || report.status === 'closed'
      )
    ) ||
    byArrival(withValues.filter((report) => report.status === 'in_trip')) ||
    byArrival(withValues)

  return {
    mileage_after: hasTripValue(lastReport?.mileage_after)
      ? lastReport!.mileage_after
      : null,
    fuel_after: hasTripValue(lastReport?.fuel_after)
      ? lastReport!.fuel_after
      : null,
  }
}

/** TIMEX-353: ПробегПосле / ТопливоПосле ближайшего по дате приезда Поданного или Закрытого */
export const fetchCarLastMileageAfter = createAsyncThunk(
  'reports/fetchCarLastMileageAfter',
  async (
    {
      carId,
      excludeReportId,
      beforeArrival,
    }: {
      carId: number | string
      excludeReportId?: number | string | null
      beforeArrival?: string | null
    },
    { getState, rejectWithValue }
  ): Promise<CarLastTripPrefill> => {
    try {
      const params = new URLSearchParams()
      params.append('car_ids', String(carId))
      params.append('order_direction', 'desc')

      const response = await axiosInstance.get(`/${URL.report}`, { params })

      if (response.status !== 200 && response.statusText !== 'OK') {
        throw new Error(response.statusText || String(response.status))
      }

      const fromApi = extractReportsList(response.data)
      if (fromApi.length) {
        return pickLastTripPrefill(fromApi, {
          excludeReportId,
          beforeArrival,
        })
      }

      const fromStore = (
        (getState() as RootState).reports.reports || []
      ).filter((report) => Number(reportCarId(report)) === Number(carId))

      return pickLastTripPrefill(fromStore, {
        carId,
        excludeReportId,
        beforeArrival,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data) as never
      }
      console.error('An unexpected error occurred:', error)
      return rejectWithValue('An unexpected error occurred') as never
    }
  }
)

export const fetchAddReports = createAsyncThunk(
  'reports/fetchAddReports',
  async (
    {
      departure,
      mileageBefore,
      car,
      fuelBefore,
      fuelAfter,
      mileageAfter,
      trailer,
      crossingBorderThere,
      crossingBorderBack,
      avgCons,
      reimbursementDate,
      id,
      status,
      issuedByn,
      issuedEur,
      issuedRub,
      issuedUsd,
      user,
      arrival,
      number,
      from,
      to,
      jobTitle,
      travelPurpose,
      travelAllowance,
    }: any,
    { rejectWithValue }
  ) => {
    const formData = new FormData()
    formData.append('departure', departure)
    // TIMEX-351: грузовик и пробег до необязательны
    formData.append('car_id', car || '')
    formData.append(
      'mileage_before',
      status === 'draft' || !status
        ? ''
        : mileageBefore !== undefined &&
            mileageBefore !== null &&
            mileageBefore !== ''
          ? String(mileageBefore)
          : ''
    )
    formData.append('from', String(from))
    formData.append('to', String(to))

    if (issuedByn) formData.append('issued_byn', issuedByn)
    if (issuedEur) formData.append('issued_eur', issuedEur)
    if (issuedRub) formData.append('issued_rub', issuedRub)
    if (issuedUsd) formData.append('issued_usd', issuedUsd)

    if (arrival) formData.append('arrival', arrival)
    if (number) formData.append('number', number)

    if (status) formData.append('status', status)
    const isDraftPayload = status === 'draft' || !status
    formData.append(
      'fuel_before',
      isDraftPayload ? '' : fuelBefore || 0
    )
    formData.append('fuel_after', isDraftPayload ? '' : fuelAfter || 0)

    formData.append(
      'mileage_after',
      isDraftPayload
        ? ''
        : mileageAfter !== undefined &&
            mileageAfter !== null &&
            mileageAfter !== ''
          ? String(mileageAfter)
          : ''
    )
    formData.append('trailer_id', trailer)
    if (crossingBorderThere)
      formData.append('crossing_border_there', crossingBorderThere)
    if (crossingBorderBack)
      formData.append('crossing_border_back', crossingBorderBack)
    if (user) {
      formData.append('user_id', user)
    } else {
      formData.append('user_id', '99')
    }

    formData.append(
      'avg_cons',
      !isNaN(avgCons) && Boolean(avgCons) && isFinite(avgCons)
        ? avgCons
        : '0.01'
    )
    if (reimbursementDate)
      formData.append('reimbursement_date', reimbursementDate)

    if (jobTitle !== undefined && jobTitle !== null) {
      formData.append('job_title', jobTitle)
    }
    if (travelPurpose !== undefined && travelPurpose !== null) {
      formData.append('travel_purpose', travelPurpose)
    }

    if (
      travelAllowance !== undefined &&
      travelAllowance !== null &&
      travelAllowance !== ''
    ) {
      formData.append('travel_allowance', travelAllowance)
    }

    if (id) {
      formData.set('_method', 'put')
    }

    try {
      const response = await axiosInstance.post(
        `/${URL.report}${id ? `/${id}` : ''}`,
        formData
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        return response.data
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(error.response?.data.message)
      } else {
        console.error('An unexpected error occurred:', error)
        return rejectWithValue('An unexpected error occurred')
      }
    }
  }
)

export const fetchReportFileByType = createAsyncThunk(
  'reports/fetchReportFile',
  async (
    { dateFrom, dateTo, exportFormat, getCars, getEmployees }: any,

    { rejectWithValue, getState }
  ) => {
    try {
      const params = new URLSearchParams()

      params.append('date_from', dateFrom)
      params.append('date_to', dateTo)
      params.append('export', exportFormat)

      getCars.forEach((car: string) => {
        params.append('cars[]', car)
      })

      getEmployees.forEach((employee: string) => {
        params.append('employees[]', employee)
      })
      let response
      response = await axiosInstance.get(`/${URL.report}/${exportFormat}`, {
        params,
        responseType: 'blob',
      })

      if (response.statusText === 'OK' || response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `report.${exportFormat}`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        return response.data
      }

      throw new Error('Ошибка при получении файла')
    } catch (error) {
      console.error('Ошибка при получении файла:', error)
      return rejectWithValue('Ошибка при получении файла')
    }
  }
)

// export const fetchReportFile = createAsyncThunk(
//   'reports/fetchReportFile',
//   async (id: number, { rejectWithValue, getState }) => {
//     try {
//       const {
//         reports: { currentReportId },
//       } = getState() as RootState

//       if (!currentReportId) {
//         throw new Error('Путевой не найден')
//       }

//       const response = await axiosInstance.get(
//         `/${URL.report}/${id || currentReportId}/pdf`,
//         {
//           responseType: 'blob',
//           headers: {
//             Accept: 'application/pdf',
//           },
//         }
//       )

//       if (response.status === 200) {
//         const url = window.URL.createObjectURL(new Blob([response.data]))
//         const link = document.createElement('a')
//         link.href = url
//         link.setAttribute('download', 'report.pdf')
//         document.body.appendChild(link)
//         link.click()
//         link.remove()
//         window.URL.revokeObjectURL(url)
//         return response.data
//       } else {
//         throw new Error('Ошибка при получении файла')
//       }
//     } catch (error: any) {
//       console.error('Ошибка при получении файла:', error)
//       if (error?.response) {
//         console.error('Response data:', error.response.data)
//         console.error('Response status:', error.response.status)
//         console.error('Response headers:', error.response.headers)
//       } else if (error.request) {
//         console.error('Request data:', error.request)
//       } else {
//         console.error('Error message:', error.message)
//       }
//       return rejectWithValue('Ошибка при получении файла')
//     }
//   }
// )

export const fetchReportFile = createAsyncThunk(
  'reports/fetchReportFile',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const {
        reports: { currentReportId },
      } = getState() as RootState

      if (!currentReportId) {
        throw new Error('Путевой не найден')
      }
      const response = await axiosInstance.get(
        `/${URL.report}/${id || currentReportId}/pdf`,
        {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
          },
        }
      )

      if (response.status === 200) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
        )
        openPdf(url, 'PDF')
        return response.data
      } else {
        throw new Error('Ошибка при получении файла')
      }
    } catch (error: any) {
      console.error('Ошибка при получении файла:', error)
      if (error?.response) {
        console.error('Response data:', error.response.data)
        console.error('Response status:', error.response.status)
        console.error('Response headers:', error.response.headers)
      } else if (error.request) {
        console.error('Request data:', error.request)
      } else {
        console.error('Error message:', error.message)
      }
      return rejectWithValue('Ошибка при получении файла')
    }
  }
)

export const fetchDeleteReport = createAsyncThunk(
  'reports/fetchDeleteReport',
  async (_, thunkAPI) => {
    const {
      reports: { currentReportId },
    } = thunkAPI.getState() as RootState

    try {
      const response = await axiosInstance.delete(
        `/${URL.report}/${currentReportId}`
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        return response.data
      }
      throw new Error('Не удалось отправить данные')
    } catch (error) {
      console.error('Ошибка при отправке данных:', error)
      return thunkAPI.rejectWithValue('Ошибка при отправке данных')
    }
  }
)

// expenses
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (reportId: number | string, thunkAPI) => {
    try {
      const response: AxiosResponse = await axiosInstance.get(
        `/${URL.report}/${reportId}/expenses`
      )

      if (response.statusText === 'OK') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при получении данных')
    }
  }
)

export const fetchAddExpense = createAsyncThunk(
  'expenses/fetchAddExpense',
  async (
    {
      type,
      payment_type,
      date,
      quantity,
      amount,
      currency,
      description,
      id,
      images,
    }: any,
    thunkAPI
  ) => {
    const {
      reports: { currentReportId, lastReportsQuery },
    } = thunkAPI.getState() as RootState

    const formData = new FormData()

    formData.append('type', type)
    formData.append('payment_type', payment_type)
    formData.append('date', date)

    formData.append('quantity', quantity || 0)
    if (amount) formData.append('amount', amount)
    if (currency) formData.append('currency', currency)
    formData.append('description', description || '')

    if (images?.length > 0) {
      //@ts-ignore
      images.forEach((photo, index) => {
        formData.append(`images[${index}]`, photo)
      })
    }

    if (id) {
      formData.set('_method', 'put')
    }

    try {
      const response = await axiosInstance.post(
        `/${URL.report}/${currentReportId}/expenses${id ? `/${id}` : ''}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        thunkAPI.dispatch(fetchReports(lastReportsQuery))
        if (currentReportId) {
          thunkAPI.dispatch(fetchCurrentReport(currentReportId))
        }

        return response.data
      }
      throw new Error('Не удалось создать запись')
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при добавлении данных')
    }
  }
)

export const fetchDeleteExpense = createAsyncThunk(
  'expenses/fetchDeleteExpense',
  async (_, thunkAPI) => {
    const {
      reports: { currentReportId, currentExpenses, lastReportsQuery },
    } = thunkAPI.getState() as RootState
    try {
      const response = await axiosInstance.delete(
        `/${URL.report}/${currentReportId}/expenses/${currentExpenses?.id}`
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        thunkAPI.dispatch(fetchReports(lastReportsQuery))
        if (currentReportId) {
          thunkAPI.dispatch(fetchCurrentReport(currentReportId))
        }

        return { ...response.data, id: currentExpenses?.id }
      }
      throw new Error('Не удалось удалить запись')
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при удалении данных')
    }
  }
)

export const fetchDeleteExpenseImage = createAsyncThunk(
  'expenses/fetchDeleteExpense',
  async ({ imageId, currentExpensesId }: any, thunkAPI) => {
    const {
      reports: { currentReportId, lastReportsQuery },
    } = thunkAPI.getState() as RootState
    try {
      // eports/5/expenses/14/image/10

      const response = await axiosInstance.delete(
        `/${URL.report}/${currentReportId}/expenses/${currentExpensesId}/image/${imageId}`
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        // Convert date strings back to Date objects for the API call
        const queryWithDates = lastReportsQuery
          ? {
            ...lastReportsQuery,
            dateFrom: lastReportsQuery.dateFrom
              ? new Date(lastReportsQuery.dateFrom)
              : undefined,
            dateTo: lastReportsQuery.dateTo
              ? new Date(lastReportsQuery.dateTo)
              : undefined,
          }
          : null
        thunkAPI.dispatch(fetchReports(queryWithDates))
        if (currentReportId) {
          thunkAPI.dispatch(fetchCurrentReport(currentReportId))
        }

        return { ...response.data, id: currentExpensesId }
      }
      throw new Error('Не удалось удалить запись')
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при удалении данных')
    }
  }
)

// Incoming
export const fetchIncoming = createAsyncThunk(
  'incoming/fetchIncoming',
  async (reportId: number | string, thunkAPI) => {
    try {
      const response: AxiosResponse = await axiosInstance.get(
        `/${URL.report}/${reportId}/incoming`
      )

      if (response.statusText === 'OK') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при получении данных')
    }
  }
)

export const fetchAddIncoming = createAsyncThunk(
  'incoming/fetchAddIncoming',
  async ({ date, amount, currency, description, id }: any, thunkAPI) => {
    const {
      reports: { currentReportId, lastReportsQuery },
    } = thunkAPI.getState() as RootState

    const formData = new FormData()

    formData.append('date', date)

    if (amount) formData.append('amount', amount)
    if (currency) formData.append('currency', currency)
    formData.append('description', description || '')

    if (id) {
      formData.set('_method', 'put')
    }
    //dev.alex-logistic.com/api/reports/82/incoming
    try {
      const response = await axiosInstance.post(
        `/${URL.report}/${currentReportId}/incomings${id ? `/${id}` : ''}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        // Convert date strings back to Date objects for the API call
        const queryWithDates = lastReportsQuery
          ? {
            ...lastReportsQuery,
            dateFrom: lastReportsQuery.dateFrom
              ? new Date(lastReportsQuery.dateFrom)
              : undefined,
            dateTo: lastReportsQuery.dateTo
              ? new Date(lastReportsQuery.dateTo)
              : undefined,
          }
          : null
        thunkAPI.dispatch(fetchReports(queryWithDates))
        if (currentReportId) {
          thunkAPI.dispatch(fetchCurrentReport(currentReportId))
        }

        return response.data
      }
      throw new Error('Не удалось создать запись')
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при добавлении данных')
    }
  }
)

export const fetchDeleteIncoming = createAsyncThunk(
  'incoming/fetchDeleteIncoming',
  async (_, thunkAPI) => {
    const {
      reports: { currentReportId, currentIncoming, lastReportsQuery },
    } = thunkAPI.getState() as RootState
    try {
      const response = await axiosInstance.delete(
        `/${URL.report}/${currentReportId}/incomings/${currentIncoming?.id}`
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        // Convert date strings back to Date objects for the API call
        const queryWithDates = lastReportsQuery
          ? {
            ...lastReportsQuery,
            dateFrom: lastReportsQuery.dateFrom
              ? new Date(lastReportsQuery.dateFrom)
              : undefined,
            dateTo: lastReportsQuery.dateTo
              ? new Date(lastReportsQuery.dateTo)
              : undefined,
          }
          : null
        thunkAPI.dispatch(fetchReports(queryWithDates))
        if (currentReportId) {
          thunkAPI.dispatch(fetchCurrentReport(currentReportId))
        }

        return { ...response.data, id: currentIncoming?.id }
      }
      throw new Error('Не удалось удалить запись')
    } catch (error) {
      return thunkAPI.rejectWithValue('Ошибка при удалении данных')
    }
  }
)

export const fetchReportGroupedCargos = createAsyncThunk(
  'reports/fetchReportGroupedCargos',
  async (
    {
      reportId,
      direction,
    }: { reportId: number | string; direction: ReportCargoDirection },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get<GroupedCargosResponse>(
        `/${URL.report}/${reportId}/cargos/grouped`,
        { params: { direction } }
      )

      if (response.statusText === 'OK') {
        return { direction, data: response.data }
      }
      throw new Error(response.statusText)
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(
          (error.response?.data as { message?: string })?.message ||
          error.message
        )
      }
      return rejectWithValue('Ошибка при получении грузов')
    }
  }
)

export const fetchAllReportGroupedCargos = createAsyncThunk(
  'reports/fetchAllReportGroupedCargos',
  async (reportId: number | string, { rejectWithValue }) => {
    try {
      const [forwardRes, backwardRes] = await Promise.all([
        axiosInstance.get<GroupedCargosResponse>(
          `/${URL.report}/${reportId}/cargos/grouped`,
          { params: { direction: 'forward' as ReportCargoDirection } }
        ),
        axiosInstance.get<GroupedCargosResponse>(
          `/${URL.report}/${reportId}/cargos/grouped`,
          { params: { direction: 'backward' as ReportCargoDirection } }
        ),
      ])

      const ok = (r: { status: number; statusText: string }) =>
        r.status === 200 || r.statusText === 'OK'

      if (!ok(forwardRes) || !ok(backwardRes)) {
        throw new Error(forwardRes.statusText || backwardRes.statusText)
      }

      return {
        forward: forwardRes.data,
        backward: backwardRes.data,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(
          (error.response?.data as { message?: string })?.message ||
          error.message
        )
      }
      return rejectWithValue('Ошибка при получении грузов')
    }
  }
)

export const fetchReportCargosClientOrder = createAsyncThunk(
  'reports/fetchReportCargosClientOrder',
  async (
    {
      reportId,
      body,
    }: {
      reportId: number | string
      body: ReportCargosClientOrderBody
      silentLoadingPage?: boolean
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/${URL.report}/${reportId}/cargos/client-order`,
        body
      )

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        return response.data
      }
      throw new Error(response.statusText)
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error)
        return rejectWithValue(
          (error.response?.data as { message?: string })?.message ||
          error.message
        )
      }
      return rejectWithValue('Ошибка при сохранении порядка клиентов')
    }
  }
)

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setCurrentReport: (state, action) => {
      return { ...state, currentReport: action.payload }
    },
    setCurrentReportId: (state, action) => {
      return { ...state, currentReportId: action.payload }
    },
    setReportModalInitialTab: (state, action: PayloadAction<number | null>) => {
      return { ...state, reportModalInitialTab: action.payload }
    },
    setIsVisibleModalReport: (state, action: { payload: boolean }) => {
      return {
        ...state,
        isVisibleModalReport: action.payload,
        currentReport: action.payload ? state.currentReport : null,
      }
    },
    setIsVisibleDeleteModalReport: (state, action: { payload: boolean }) => ({
      ...state,
      isVisibleDeleteModalReport: action.payload,
    }),
    // expenses
    setCurrentExpenses: (state, action) => {
      return { ...state, currentExpenses: action.payload }
    },
    setCurrentIncoming: (state, action) => {
      return { ...state, currentIncoming: action.payload }
    },
    clearExpenses: (state) => {
      return { ...state, expenses: [] }
    },
    setVisibleDeleteModalExpenses: (state, action: { payload: boolean }) => ({
      ...state,
      isVisibleDeleteModalExpenses: action.payload,
    }),
    setVisibleDeleteModalIncoming: (state, action: { payload: boolean }) => ({
      ...state,
      isVisibleDeleteModalIncoming: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state, action) => {
        return { ...state, isPendingGetReports: true }
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        // Convert Date objects to strings to avoid serialization issues
        const serializedQuery = action.meta.arg
          ? {
            ...action.meta.arg,
            dateFrom:
              action.meta.arg.dateFrom instanceof Date
                ? action.meta.arg.dateFrom.toISOString()
                : action.meta.arg.dateFrom,
            dateTo:
              action.meta.arg.dateTo instanceof Date
                ? action.meta.arg.dateTo.toISOString()
                : action.meta.arg.dateTo,
          }
          : null

        return {
          ...state,
          reports: filterReportsForDriverVisibility(action.payload),
          lastReportsQuery: serializedQuery,
          isPendingGetReports: false,
        }
      })
      .addCase(fetchReports.rejected, (state, action) => {
        toast.error(action.payload as string)

        return { ...state, isPendingGetReports: false }
      })
      .addCase(fetchReportsSummary.pending, (state, action) => ({
        ...state,
        isPendingGetReportsSummary: true,
        reportsSummaryRequestId: action.meta.requestId,
      }))
      .addCase(fetchReportsSummary.fulfilled, (state, action) => {
        if (state.reportsSummaryRequestId !== action.meta.requestId) {
          return state
        }
        return {
          ...state,
          reportsSummary: action.payload,
          isPendingGetReportsSummary: false,
        }
      })
      .addCase(fetchReportsSummary.rejected, (state, action) => {
        if (state.reportsSummaryRequestId !== action.meta.requestId) {
          return state
        }
        // Не затираем прошлые цифры нулями при ошибке сети
        return {
          ...state,
          isPendingGetReportsSummary: false,
        }
      })

      .addCase(fetchAllReports.pending, (state, action) => {
        return { ...state, isPendingGetAllReports: true }
      })
      .addCase(fetchAllReports.fulfilled, (state, action) => {
        return {
          ...state,
          reports: filterReportsForDriverVisibility(action.payload),
          isPendingGetAllReports: false,
        }
      })
      .addCase(fetchAllReports.rejected, (state, action) => {
        toast.error(action.payload as string)

        return { ...state, isPendingGetAllReports: false }
      })

      .addCase(fetchCurrentReport.pending, (state, action) => {
        return { ...state, isPendingGetCurrentReports: true }
      })
      .addCase(fetchCurrentReport.fulfilled, (state, action) => {
        return {
          ...state,
          currentReport: action.payload,
          isPendingGetCurrentReports: false,
        }
      })
      .addCase(fetchCurrentReport.rejected, (state, action) => {
        toast.error(action.payload as string)

        return { ...state, isPendingGetCurrentReports: false }
      })

      .addCase(fetchAddReports.pending, (state, action) => {
        return state
      })
      .addCase(fetchAddReports.fulfilled, (state, action) => {
        toast.success('Сохранено!')
        const isUserExist = current(state).reports.find(
          (item) => item.id === action.payload.id
        )

        if (isUserExist) {
          return {
            ...state,
            isVisibleModalReport: false,
            currentReport: null,
            currentReportId: null,
            reports: filterReportsForDriverVisibility(
              state.reports.map((item) =>
                item.id === action.payload.id ? action.payload : item
              )
            ),
          }
        }
        return {
          ...state,
          reports: filterReportsForDriverVisibility(
            sortByDate([action.payload, ...state.reports])
          ),
          isVisibleModalReport: false,
          currentReport: null,
          currentReportId: null,
        }
      })
      .addCase(fetchAddReports.rejected, (state, action) => {
        toast.error(action.payload as string)
        return {
          ...state,
          isVisibleModalReport: false,
          currentReport: null,
          currentReportId: null,
        }
      })
      .addCase(fetchReportFile.pending, (state, action) => {
        return state
      })
      .addCase(fetchReportFile.fulfilled, (state, action) => {
        return state
      })
      .addCase(fetchReportFile.rejected, (state, action) => {
        toast.error(action.payload as string)

        return state
      })

    builder.addCase(fetchDeleteReport.pending, (state) => {
      return state
    })
    builder.addCase(fetchDeleteReport.fulfilled, (state, action) => {
      toast.success('Удалено!')

      return {
        ...state,
        isVisibleDeleteModalReport: false,
        currentReport: null,
        reports: state.reports.filter(
          (item) => item.id !== state?.currentReportId
        ),
      }
    })
    builder
      .addCase(fetchDeleteReport.rejected, (state, action) => {
        toast.error(action.payload as string)
        return { ...state, isVisibleDeleteModalReport: false }
      })

      // expensers
      .addCase(fetchExpenses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        fetchExpenses.fulfilled,
        (state, action: PayloadAction<Expense[]>) => {
          state.expenses = action.payload
          state.isLoading = false
        }
      )
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchAddExpense.pending, (state) => {
        state.isPendingAddExpense = true
      })
      .addCase(fetchAddExpense.fulfilled, (state, action) => {
        toast.success('Сохранено!')

        if (state.currentReport) {
          const isExist = state.currentReport.expenses?.find(
            (expense) => expense.id === action.payload.id
          )

          if (isExist) {
            state.currentReport.expenses = state.currentReport.expenses.map(
              (expense) =>
                expense.id === action.payload.id ? action.payload : expense
            )

            // state.reports = state.reports.map((report) =>
            //   //@ts-ignore
            //   report.id === state?.currentReport.id
            //     ? state.currentReport
            //     : report
            // )
          } else {
            // state.reports = state.reports.map((report) =>
            //   //@ts-ignore
            //   report.id === state?.currentReport.id
            //     ? state.currentReport
            //     : report
            // )

            state.currentReport.expenses = [
              action.payload,
              ...(state.currentReport.expenses || []),
            ]
          }
        }

        state.isPendingAddExpense = false
      })
      .addCase(fetchAddExpense.rejected, (state, action) => {
        state.isPendingAddExpense = false
        state.isRejectAddExpense = true
        state.error = action.payload as string
      })
      .addCase(fetchDeleteExpense.pending, (state) => {
        state.isPendingDeleteExpense = true
      })

      .addCase(
        fetchDeleteExpense.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          toast.success('Удалено!')

          if (state.currentReport) {
            state.currentReport.expenses = state.currentReport.expenses.filter(
              (expense) => expense.id !== action.payload.id
            )
            state.reports = state.reports.map((report) =>
              //@ts-ignore
              report.id === state?.currentReport.id
                ? state.currentReport
                : report
            )
          }
          state.isVisibleDeleteModalExpenses = false
          state.isPendingDeleteExpense = false
          state.isFulfilledDeleteExpense = true
        }
      )

      .addCase(fetchDeleteExpense.rejected, (state, action) => {
        toast.error(action.payload as string)

        state.isVisibleDeleteModalExpenses = false
        state.isPendingDeleteExpense = false
        state.isRejectDeleteExpense = true
        state.error = action.payload as string
      })

      // INCOMING
      .addCase(fetchAddIncoming.pending, (state) => {
        state.isPendingAddIncoming = true
      })
      .addCase(fetchAddIncoming.fulfilled, (state, action) => {
        toast.success('Сохранено!')

        if (state.currentReport) {
          const isExist = state.currentReport.incomings?.find(
            (incoming) => incoming.id === action.payload.id
          )

          if (isExist) {
            state.currentReport.incomings = state.currentReport.incomings.map(
              (incoming) =>
                incoming.id === action.payload.id ? action.payload : incoming
            )

            // state.reports = state.reports.map((report) =>
            //   //@ts-ignore
            //   report.id === state?.currentReport.id
            //     ? state.currentReport
            //     : report
            // )
          } else {
            // state.reports = state.reports.map((report) =>
            //   //@ts-ignore
            //   report.id === state?.currentReport.id
            //     ? state.currentReport
            //     : report
            // )

            state.currentReport.incomings = [
              action.payload,
              ...(state.currentReport.incomings || []),
            ]
          }
        }
        state.isPendingAddIncoming = false
      })
      .addCase(fetchAddIncoming.rejected, (state, action) => {
        state.isPendingAddIncoming = false
        state.isRejectAddIncoming = true
        state.error = action.payload as string
      })

      .addCase(fetchDeleteIncoming.pending, (state) => {
        state.isPendingDeleteIncoming = true
      })

      .addCase(
        fetchDeleteIncoming.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          toast.success('Удалено!')

          if (state.currentReport) {
            state.currentReport.incomings =
              state.currentReport.incomings.filter(
                (incoming) => incoming.id !== action.payload.id
              )
            state.reports = state.reports.map((report) =>
              //@ts-ignore
              report.id === state?.currentReport.id
                ? state.currentReport
                : report
            )
          }
          state.isVisibleDeleteModalIncoming = false
          state.isPendingDeleteIncoming = false
          state.isFulfilledDeleteIncoming = true
        }
      )

      .addCase(fetchDeleteIncoming.rejected, (state, action) => {
        toast.error(action.payload as string)

        state.isVisibleDeleteModalIncoming = false
        state.isPendingDeleteIncoming = false
        state.isRejectDeleteIncoming = true
        state.error = action.payload as string
      })

      .addCase(fetchAllReportGroupedCargos.pending, (state) => {
        state.isPendingGroupedCargos = true
      })
      .addCase(
        fetchAllReportGroupedCargos.fulfilled,
        (
          state,
          action: PayloadAction<{
            forward: GroupedCargosResponse
            backward: GroupedCargosResponse
          }>
        ) => {
          state.isPendingGroupedCargos = false
          state.groupedCargosForward = action.payload.forward
          state.groupedCargosBackward = action.payload.backward
          state.isPendingReportCargosClientOrder = false
        }
      )
      .addCase(fetchAllReportGroupedCargos.rejected, (state, action) => {
        toast.error((action.payload as string) || 'Ошибка при загрузке грузов')
        state.isPendingGroupedCargos = false
        state.isPendingReportCargosClientOrder = false
      })

      .addCase(fetchReportGroupedCargos.pending, (state) => {
        state.isPendingGroupedCargos = true
      })
      .addCase(
        fetchReportGroupedCargos.fulfilled,
        (
          state,
          action: PayloadAction<{
            direction: ReportCargoDirection
            data: GroupedCargosResponse
          }>
        ) => {
          state.isPendingGroupedCargos = false
          state.isPendingReportCargosClientOrder = false
          if (action.payload.direction === 'forward') {
            state.groupedCargosForward = action.payload.data
          } else {
            state.groupedCargosBackward = action.payload.data
          }
        }
      )
      .addCase(fetchReportGroupedCargos.rejected, (state, action) => {
        toast.error((action.payload as string) || 'Ошибка при загрузке грузов')
        state.isPendingGroupedCargos = false
        state.isPendingReportCargosClientOrder = false
      })

      .addCase(fetchReportCargosClientOrder.pending, (state, action) => {
        if (!action.meta.arg?.silentLoadingPage) {
          state.isPendingReportCargosClientOrder = true
        }
      })
      .addCase(fetchReportCargosClientOrder.fulfilled, (state) => {
        /* isPendingReportCargosClientOrder снимается после grouped refetch */
        // toast.success('Порядок клиентов сохранён')
      })
      .addCase(fetchReportCargosClientOrder.rejected, (state, action) => {
        if (!action.meta.arg?.silentLoadingPage) {
          state.isPendingReportCargosClientOrder = false
        }
        toast.error((action.payload as string) || 'Ошибка сохранения порядка')
      })
  },
})

export const {
  setCurrentReport,
  setIsVisibleModalReport,
  setIsVisibleDeleteModalReport,
  setReportModalInitialTab,
  clearExpenses,
  setVisibleDeleteModalExpenses,
  setCurrentExpenses,
  setCurrentReportId,

  setCurrentIncoming,
  setVisibleDeleteModalIncoming,
} = reportsSlice.actions

export default reportsSlice.reducer
